import { STAR_CHEST_SPAWN_INCREMENT } from '../../../Constants/Chests';
import { nextPetLevelLimit, PetAnimationData, PetData, PetId, PET_ANIMATION_PATH } from '../../../Constants/Pets';
import Game from '../../../Game';
import { IGameState } from '../../../State/Game/IGameState';
import { Random } from '../../Random';
import { addDragonbonesResources, areDBResourcesLoaded } from '../../Resource/ResourceHelper';
import { WorldDataHelper } from '../../WorldDataHelper';
import { IPetAnimationData } from './IPetAnimationData';
import { IPetData } from './IPetData';
import { PetAnimations } from './PetAnimations';
import { IPlayerState } from '../../../State/Player/IPlayerState';
import { LuckyWheelStateHelper } from 'State/Player/LuckyWheel/LuckyWheelStateHelper';

export class PetsHelper {
    public static async loadPetAnimations(id: PetId, dragonbonesName: string): Promise<string> {
        const DBName: string = dragonbonesName ?? PetsHelper.getDragonbonesName(id);

        if (areDBResourcesLoaded(DBName)) return DBName;

        addDragonbonesResources(DBName, PET_ANIMATION_PATH);
        await Game.resource.load();

        return DBName;
    }

    public static getDragonbonesName(id: PetId): string {
        return `pet_animations_${id}`;
    }

    public static getPetAnimationData(status: PetAnimations): IPetAnimationData {
        return (
            PetAnimationData[status] ?? {
                armatureName: undefined,
                animationName: undefined,
            }
        );
    }

    public static getPetData(petId: PetId): IPetData {
        return PetData[petId];
    }

    public static hasUnlockedAPet(pState: IPlayerState): boolean {
        return pState.petsState.unlockedPets.length > 0;
    }

    public static getNextPetLevel(state: IGameState): { currentWorldMaxLevel: number; nextPetLevel: number } {
        const possibleNextPetLevels: number[] = [];

        const currentLevel = Game.player.getNextLevelInWorld(state.worldState.worldNumber, state);

        const { minLimit, maxLimit } = nextPetLevelLimit;
        const firstPossibleLevel = currentLevel + minLimit;
        const lastPossibleLevel = currentLevel + maxLimit;

        const currentWorldMaxLevel = WorldDataHelper.maxLevel(state.worldState.worldNumber);
        for (let i = firstPossibleLevel; i <= lastPossibleLevel; i++) {
            if (canBeNextPetLevel(i)) possibleNextPetLevels.push(i);
        }

        let nextPetLevel: number;
        if (possibleNextPetLevels.length > 0) {
            const rand = Random.int(0, possibleNextPetLevels.length - 1);
            nextPetLevel = possibleNextPetLevels[rand];
        } else {
            let nextPossibleLevel: number = lastPossibleLevel;
            // add 30 level safety limit on while-loop
            // fixme: declare 30 level constant somewhere better
            while (!nextPetLevel && nextPossibleLevel <= currentWorldMaxLevel + 30) {
                nextPossibleLevel++;
                nextPetLevel = canBeNextPetLevel(nextPossibleLevel) ? nextPossibleLevel : undefined;
            }
        }

        return {
            currentWorldMaxLevel,
            nextPetLevel,
        };

        function canBeNextPetLevel(possibleLevel: number): boolean {
            if (WorldDataHelper.isPlinkoLevel(possibleLevel)) return false;

            const hasStarChest = possibleLevel % STAR_CHEST_SPAWN_INCREMENT === 0;
            const hasluckyWheel = LuckyWheelStateHelper.getLuckyWheelLevels(state.playerState.lastClaimedSpinLevel, currentWorldMaxLevel).includes(possibleLevel);
            let hasCropUnlock = undefined;

            if (possibleLevel <= 120) {
                const worldNumber =
                    possibleLevel > currentWorldMaxLevel
                        ? state.worldState.worldNumber + 1
                        : state.worldState.worldNumber;
                const fieldData = WorldDataHelper.getWorldFieldData(worldNumber);

                if (!fieldData) {
                    return false;
                }

                fieldData.forEach(field => {
                    if (field.requiredLevel.includes(possibleLevel)) {
                        hasCropUnlock = true;
                    }
                });
            } else {
                // fixme: do we have a place for the "crops unlock every N levels" constant?
                hasCropUnlock = possibleLevel % 15 === 0;
            }

            return !hasCropUnlock && !hasStarChest && !hasluckyWheel;
        }
    }
}
